/**
 * App-version comparison + the "am I the latest?" client check.
 *
 * Deliberately dependency-free: the versions we compare here are the plain
 * `major.minor.patch` strings baked into each app at build time (occasionally
 * two-part like "1.0"), so a numeric field compare is exact and there's no
 * reason to pull in a full semver library. Pre-release / build metadata
 * (anything after `-` or `+`) is stripped and ignored — the version gate only
 * cares about the release triple.
 *
 * This module is the single source of truth for the version check shared by the
 * web, kiosk and installer apps. (The field Android app carries its own tiny
 * mirror because it doesn't depend on this package.)
 */

export interface Semver {
  major: number;
  minor: number;
  patch: number;
}

/**
 * Parse `"1.2.3"` — also `"1.2"`, `"1"`, `"v1.2.3"`, `"1.2.3-rc.1"` — into its
 * numeric parts. A leading `v` and any pre-release/build suffix are ignored;
 * missing trailing parts default to 0. Returns `null` when there's no leading
 * `major(.minor(.patch))` to read, so callers can treat "unparseable" as
 * "can't judge → don't block".
 */
export function parseSemver(input: string): Semver | null {
  if (typeof input !== 'string') return null;
  const cleaned = input.trim().replace(/^v/i, '').split(/[-+]/, 1)[0] ?? '';
  if (!/^\d+(\.\d+){0,2}$/.test(cleaned)) return null;
  const [major = 0, minor = 0, patch = 0] = cleaned
    .split('.')
    .map((n) => Number.parseInt(n, 10));
  return { major, minor, patch };
}

/**
 * Compare two versions: `-1` if `a < b`, `1` if `a > b`, `0` if equal.
 * Unparseable inputs sort as `0.0.0`.
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const pa = parseSemver(a) ?? { major: 0, minor: 0, patch: 0 };
  const pb = parseSemver(b) ?? { major: 0, minor: 0, patch: 0 };
  for (const key of ['major', 'minor', 'patch'] as const) {
    if (pa[key] < pb[key]) return -1;
    if (pa[key] > pb[key]) return 1;
  }
  return 0;
}

/**
 * True when `current` is strictly older than `minimum` — i.e. the running build
 * is below the floor the server accepts and must be hard-blocked until updated.
 *
 * Fails OPEN: if either version is unparseable, returns `false`. A version we
 * can't read must never lock a user out of the app.
 */
export function isBelowMinimum(current: string, minimum: string): boolean {
  if (parseSemver(current) === null || parseSemver(minimum) === null) return false;
  return compareSemver(current, minimum) < 0;
}

/** Decision returned by {@link checkAppVersion}. */
export interface VersionCheckResult {
  /** True → the running build is below `minVersion` and should be blocked. */
  blocked: boolean;
  /** The build version we compared (echoed back for display). */
  currentVersion: string;
  /** Latest version the server advertises, or `null` if the check couldn't run. */
  latestVersion: string | null;
  /** Minimum version the server still accepts, or `null` if unknown. */
  minVersion: string | null;
  /** Where to get the new build, when the server provides it. */
  updateUrl: string | null;
}

export interface CheckAppVersionOptions {
  /** URL of `GET …/system/version` (app-relative or absolute). */
  versionUrl: string;
  /** This build's version — inject at build time (e.g. Vite `__APP_VERSION__`). */
  currentVersion: string;
  /** Injectable fetch (tests); defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Abort the request after this many ms so a wedged server never hangs the gate. */
  timeoutMs?: number;
}

/**
 * Ask the server what the latest/minimum app version is and decide whether the
 * running build must update. Always resolves — never throws — and fails OPEN
 * (`blocked: false`) on any network error, timeout, or malformed response, so a
 * flaky or offline server can never brick the app behind an update wall.
 */
export async function checkAppVersion(
  options: CheckAppVersionOptions
): Promise<VersionCheckResult> {
  const { versionUrl, currentVersion, fetchImpl = fetch, timeoutMs = 8000 } = options;

  const failOpen: VersionCheckResult = {
    blocked: false,
    currentVersion,
    latestVersion: null,
    minVersion: null,
    updateUrl: null,
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(versionUrl, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return failOpen;
    const body = (await res.json()) as { data?: unknown };
    const data = (body?.data ?? body) as {
      version?: unknown;
      minVersion?: unknown;
      updateUrl?: unknown;
    };
    const minVersion = typeof data?.minVersion === 'string' ? data.minVersion : null;
    const latestVersion = typeof data?.version === 'string' ? data.version : null;
    const updateUrl = typeof data?.updateUrl === 'string' ? data.updateUrl : null;

    return {
      blocked: minVersion !== null && isBelowMinimum(currentVersion, minVersion),
      currentVersion,
      latestVersion,
      minVersion,
      updateUrl,
    };
  } catch {
    return failOpen;
  } finally {
    clearTimeout(timer);
  }
}
